/**
 * Per-item hover store.
 *
 * Hover used to live on the big `ItemRenderContext` memo, so every hover
 * transition changed `ctx` identity and re-rendered the entire recursive
 * `SpaceRenderer` tree — undoing the pan/zoom quantization that exists
 * precisely to avoid tree-wide re-renders.
 *
 * Instead we keep the hovered id in a tiny external store and let each
 * `ItemRenderer` subscribe through `useHovered(id)` (backed by
 * `useSyncExternalStore`). The snapshot is the boolean "am I hovered?",
 * so React only re-renders the two items whose boolean actually flips
 * (the one losing hover and the one gaining it) — not the whole tree.
 *
 * The store object identity is stable for the life of the board, so the
 * `HoverStoreProvider` value never changes and never triggers a context
 * re-render of its own.
 */
import { createContext, useContext, useSyncExternalStore } from "react";

export interface HoverStore {
  subscribe(onChange: () => void): () => void;
  getHoveredId(): string | null;
  setHoveredId(id: string | null): void;
}

export function createHoverStore(): HoverStore {
  let hoveredId: string | null = null;
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getHoveredId() {
      return hoveredId;
    },
    setHoveredId(id) {
      if (id === hoveredId) return;
      hoveredId = id;
      for (const l of listeners) l();
    },
  };
}

const HoverContext = createContext<HoverStore | null>(null);
export const HoverStoreProvider = HoverContext.Provider;

// Inert fallback for renderers mounted without a provider (e.g. the
// standalone `EdgesLayer` use in TypesMode): hover is simply always off.
const NOOP_STORE: HoverStore = {
  subscribe: () => () => {},
  getHoveredId: () => null,
  setHoveredId: () => {},
};

/** Subscribe to "is this id hovered?" — re-renders only when the boolean flips. */
export function useHovered(id: string): boolean {
  const store = useContext(HoverContext) ?? NOOP_STORE;
  return useSyncExternalStore(
    store.subscribe,
    () => store.getHoveredId() === id,
    () => false,
  );
}
